import { useState } from "react";
import { Loader2, Scale, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    useClaim,
} from "@/hooks/use-claim";
import { useAuth } from "@/hooks/use-auth";
import { useFinancialSummary } from "@/hooks/use-claim-financials";
import { useAppraisalRequest, useAppraisalScheduler } from "@/hooks/use-appraisal";
import type { ClaimModuleKey } from "@/constants/claim-options";
import { appraiserConfig } from "@/config/appraiser";
import { cn } from "@/lib/utils";

interface NeedAppraisalButtonProps {
    claimId: number;
    className?: string;
    disabled?: boolean;
    hiddenModules?: ClaimModuleKey[];
}

const REQUIRED_INFO = [
    "All listed policyholder names",
    "Carrier name",
    "Cause of loss",
    "Claim number",
    "Policy number",
    "Date of loss",
    "Home / loss address",
    "Email address",
    "Phone number",
    "Contractor / PA estimate",
    "Carrier estimate",
    "Photos / photo document / photo link",
];

const PREFERRED_INFO = [
    "Carrier coverage / denial letter (heavily preferred)",
    "Measurement report (heavily preferred)",
    "Sample reports — e.g. ITEL, NTSS (heavily preferred)",
    "Outside expert docs (engineer reports, attorney letters, etc.)",
    "Preferred attorney to compel (if represented by a PA — recommendations provided by state if none requested)",
    "Cause-of-loss verification data (e.g. storm reports)",
];

export function NeedAppraisalButton({ claimId, className, disabled = false, hiddenModules = [] }: NeedAppraisalButtonProps) {
    const [open, setOpen] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    // Claim/financial data is only used to prefill the Calendly embed, so only fetch it
    // once the dialog is open rather than on every claim page load.
    const { claim } = useClaim(claimId, { enabled: open });
    const { user } = useAuth();
    // The financial summary endpoint is gated by requireClaimModuleAccess('financials'), so
    // skip it when the current assignee has financials hidden — otherwise the deductible
    // prefill would trigger a forbidden request (and its retries) for a value we then ignore.
    const financialsHidden = hiddenModules.includes("financials");
    const { data: financialSummary } = useFinancialSummary(claimId, { enabled: open && !financialsHidden });
    const { requestAppraisal, isRequestingAppraisal } = useAppraisalRequest(claimId);
    const { calendlyUrl, requiresScheduling, hasScheduled, resetScheduling } = useAppraisalScheduler({
        claim,
        userName: user?.name,
        deductibleCents: financialsHidden ? undefined : financialSummary?.deductibleCents,
        open,
    });

    const handleOpenChange = (next: boolean) => {
        if (isRequestingAppraisal) return;
        setOpen(next);
        if (!next) {
            setAcknowledged(false);
            resetScheduling();
        }
    };

    const handleSubmit = () => {
        if (disabled) return;
        requestAppraisal({
            onSuccess: () => {
                setOpen(false);
                setAcknowledged(false);
                resetScheduling();
            },
        });
    };

    const isButtonDisabled = disabled || isRequestingAppraisal;

    return (
        <>
            <Card className={cn("w-full overflow-hidden border-primary bg-primary text-primary-foreground shadow-xs", className)}>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    disabled={isButtonDisabled}
                    className="w-full disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid="need-appraisal-button"
                >
                    <CardHeader
                        className={cn(
                            "overflow-hidden px-4 py-3",
                            isButtonDisabled
                                ? "cursor-not-allowed"
                                : "cursor-pointer transition-colors hover:bg-primary/90",
                        )}
                    >
                        <div className="flex w-full min-w-0 items-center justify-center">
                            <div className="flex min-w-0 items-center justify-center gap-2">
                                {isRequestingAppraisal ? (
                                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                                ) : (
                                    <Scale className="h-4 w-4 shrink-0" />
                                )}
                                <CardTitle className="text-sm font-medium truncate">Request an appraisal</CardTitle>
                            </div>
                        </div>
                    </CardHeader>
                </button>
            </Card>

            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent className="flex h-[100dvh] max-h-[100dvh] max-w-2xl min-h-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[100dvh] sm:max-h-[100dvh] sm:overflow-hidden">
                    <DialogHeader className="shrink-0 border-b px-6 py-4">
                        <DialogTitle>Request an appraisal</DialogTitle>
                        <DialogDescription className="sr-only">
                            Review the appraisal details and schedule a consultation before sharing this claim with {appraiserConfig.name}.
                        </DialogDescription>
                        <a
                            href={appraiserConfig.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                        >
                            Provided by {appraiserConfig.name}
                            <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                    </DialogHeader>

                    <Tabs defaultValue="details" className="flex min-h-0 flex-1 flex-col">
                        <TabsList className="mx-6 mt-3 grid w-auto shrink-0 grid-cols-2">
                            <TabsTrigger value="details">Appraisal details</TabsTrigger>
                            <TabsTrigger value="schedule">Schedule consultation</TabsTrigger>
                        </TabsList>

                        <TabsContent value="details" className="min-h-0 flex-1 overflow-y-auto px-6 py-4 text-sm">
                            <div className="space-y-5">
                            {/* Service area */}
                            <section className="space-y-2">
                                <h3 className="font-semibold text-foreground">Service area</h3>
                                <div className="flex flex-wrap gap-1.5">
                                    {appraiserConfig.serviceAreaStates.map((state) => (
                                        <span
                                            key={state}
                                            className="inline-flex items-center rounded-md border bg-muted/40 px-2 py-0.5 text-xs font-medium"
                                        >
                                            {state}
                                        </span>
                                    ))}
                                </div>
                            </section>

                            {/* Required + Preferred info (side by side on larger screens) */}
                            <div className="grid gap-5 sm:grid-cols-2">
                                <section className="space-y-2">
                                    <h3 className="font-semibold text-foreground">Required information</h3>
                                    <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                                        {REQUIRED_INFO.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </ul>
                                </section>

                                <section className="space-y-2">
                                    <h3 className="font-semibold text-foreground">Preferred information</h3>
                                    <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                                        {PREFERRED_INFO.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </ul>
                                </section>
                            </div>

                            <div className="space-y-3 border-t pt-4">
                                <p className="text-xs text-muted-foreground">
                                    Appraisal helps two unbiased third-party appraisers reach a settlement.
                                    Sharing this claim gives {appraiserConfig.name} full read/write access to
                                    view and modify it. {appraiserConfig.name} will evaluate the claim on its
                                    merits and advise you how to proceed, but does not guarantee that your exact
                                    scope will be accepted or enforced.
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {appraiserConfig.name} uses an "Honor Invoicing System," meaning no invoice
                                    is issued until services are complete. Under its strict "one-strike policy,"
                                    you are responsible for ensuring the client pays the invoice. If a collections
                                    issue arises, billing may switch to "pay-before-sign," meaning the final
                                    service product will not be provided until the invoice is paid.
                                </p>

                                <label className="flex items-start gap-2">
                                    <Checkbox
                                        checked={acknowledged}
                                        onCheckedChange={(checked) => setAcknowledged(checked === true)}
                                        className="mt-0.5"
                                        data-testid="appraisal-acknowledge-checkbox"
                                    />
                                    <span className="text-sm text-foreground">
                                        I authorize {appraiserConfig.name} to access and modify this claim,
                                        and I accept responsibility for ensuring the client pays the invoice
                                        once issued.
                                    </span>
                                </label>
                            </div>

                            </div>
                        </TabsContent>

                        <TabsContent value="schedule" className="min-h-0 flex-1 overflow-y-auto px-6 py-4 text-sm">
                            <section className="space-y-2">
                                {calendlyUrl && (
                                    <div className="overflow-hidden rounded-md border">
                                        <iframe
                                            src={calendlyUrl}
                                            title={`Schedule a consultation with ${appraiserConfig.name}`}
                                            className="h-[420px] w-full"
                                            loading="lazy"
                                        />
                                    </div>
                                )}
                                {hasScheduled && (
                                    <p className="flex items-center gap-1.5 text-sm font-medium text-green-600">
                                        <CheckCircle2 className="h-4 w-4" />
                                        Consultation scheduled
                                    </p>
                                )}
                            </section>
                        </TabsContent>
                    </Tabs>

                    <div className="shrink-0 border-t px-6 py-4">
                        {requiresScheduling && !hasScheduled && (
                            <p className="mb-3 text-xs text-muted-foreground">
                                Schedule a consultation above to enable sending.
                            </p>
                        )}

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => handleOpenChange(false)}
                                disabled={isRequestingAppraisal}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={
                                    disabled ||
                                    !acknowledged ||
                                    (requiresScheduling && !hasScheduled) ||
                                    isRequestingAppraisal
                                }
                                className="gap-2"
                                data-testid="appraisal-submit-button"
                            >
                                {isRequestingAppraisal && <Loader2 className="h-4 w-4 animate-spin" />}
                                Send to {appraiserConfig.name}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
